import { useApi } from '@src/app/context/Api';
import { useCommonCriteria } from '@src/entities/Criteria/ui/hooks/useCommonCriteria';
import { useCommonCriteriaVersions } from '@src/entities/Criteria/ui/hooks/useCommonCriteriaVersions';
import { abbrNum } from '@src/shared/includes/functions';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { isEmpty, isNull, isUndefined } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { WarningMessage } from './components/WarningMessage';
import { Styles } from './includes/styles';

/**
 * Modal: Activation
 *
 * Prompts the user with a popup window asking them if they wish to cancel or continue activating the action path.
 *
 * @param actionPath
 * @param handleActivation
 * @param isOpen
 * @param refetch
 * @param setActionPath
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function Activation({
  actionPath = {
    criteria: {
      entrance: {
        commonCriteriaVersionsIds: [],
        commonIds: [],
      },
    },
    id: '',
  },
  handleActivation = async () => {},
  isOpen = false,
  refetch = () => {},
  setActionPath = () => {},
  setIsOpen = () => {},
}) {
  const [isChecked, setChecked] = useState(false);

  const router = useRouter();

  const { useApiQuery } = useApi('ActionPath');
  const [GetActionPathCount, { data: riskRecordCount, isFetched, isFetching, isLoading }] =
    useApiQuery('GetActionPathCount');

  const {
    criteriaList,
    data: commonCriteria,
    fetch: fetchCriteria,
    isLoading: isCriteriaLoading,
  } = useCommonCriteria();

  const {
    data: commonCriteriaVersions,
    fetch: fetchCriteriaVersions,
    isLoading: isCriteriaVersionsLoading,
    versionsList,
  } = useCommonCriteriaVersions();

  /**
   * retrieve common criteria saved to the action path
   */
  useEffect(() => {
    if (!isCriteriaLoading && isEmpty(commonCriteria) && !isUndefined(actionPath?.criteria)) {
      fetchCriteria(actionPath?.criteria);
    }
  }, [isCriteriaLoading, commonCriteria, actionPath]);

  /**
   * retrieve common criteria versions saved to the action path
   */
  useEffect(() => {
    if (!isCriteriaVersionsLoading && isEmpty(commonCriteriaVersions) && !isUndefined(actionPath?.criteria)) {
      fetchCriteriaVersions(actionPath?.criteria);
    }
  }, [isCriteriaVersionsLoading, commonCriteriaVersions, actionPath]);

  /**
   * fetch the number of records associated with the action path
   */
  useEffect(() => {
    if (!isFetched && isUndefined(riskRecordCount)) {
      GetActionPathCount({ actionPathId: actionPath?.id });
    }
  }, [isFetched, riskRecordCount]);

  /**
   * loop through criteriaList and versionsList to determine if the versions are a mismatch
   * @type {*[]}
   */
  const needsReview = useMemo(() => {
    const items = {
      entrance: [],
      exit: [],
    };

    criteriaList.forEach((criteria) => {
      const version = versionsList.find((item) => {
        return criteria?.id === item?.commonCriteriaId;
      });

      if (!isUndefined(version) && criteria?.activeVersionNbr !== version?.version) {
        if (criteria?.context.includes('entrance')) {
          items.entrance.push(criteria);
        }

        if (criteria?.context.includes('exit')) {
          items.exit.push(criteria);
        }
      }
    });

    if (isEmpty(items.entrance) && isEmpty(items.exit)) {
      return {};
    }

    return items;
  }, [criteriaList, versionsList]);

  /**
   * handleActivate
   *
   * Sends the api request to activate the action path.
   *
   * @returns {Promise<void>}
   */
  const handleActivate = async () => {
    setIsOpen(false);
    setActionPath({});
    await handleActivation({ ...actionPath, ...{ status: 'ACTIVE' } }, refetch);
  };

  /**
   * handleClose
   *
   * Highlights the row in the table for the action path which was requested to activate, but the user backed out. The
   * goal is to make it easy to identify which row in the table the user attempted to take action on, in-case they
   * change their mind and want to continue with the same one again.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    setIsOpen(false);
    setActionPath({});
    router.navigate(`/`);
    router.navigate(`/action-paths?highlight=${actionPath?.id}`);
  };

  let count = riskRecordCount;

  if (riskRecordCount > 0) {
    count = abbrNum(riskRecordCount);
  }

  return (
    <ErrorHandler location="src/features/ActionPaths/Activation/Activation.jsx">
      <Modal
        footer={
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                isDisabled={
                  isLoading ||
                  isFetching ||
                  isNull(riskRecordCount) ||
                  (riskRecordCount === 0 && !isChecked) ||
                  !isEmpty(needsReview)
                }
                onClick={handleActivate}
                variant="solid"
              >
                Activate
              </Button>
            </Layout.Group>
          </Modal.Section>
        }
        isOpen={isOpen}
        onClose={handleClose}
        title="Confirm Activation"
      >
        <Styles>
          <Modal.Section>
            {!isEmpty(needsReview) && (
              <React.Fragment>
                {needsReview?.entrance?.map((theCriteria) => {
                  return (
                    <WarningMessage
                      actionPath={actionPath}
                      context="entrance"
                      criteria={theCriteria}
                      key={`entrance-${theCriteria?.id}`}
                    />
                  );
                })}
                {needsReview?.exit?.map((theCriteria, index) => {
                  return (
                    <WarningMessage
                      actionPath={actionPath}
                      context="exit"
                      criteria={theCriteria}
                      key={`exit-${theCriteria?.id}`}
                    />
                  );
                })}
              </React.Fragment>
            )}

            <Layout.Group>
              <div>The number of records associated with this Action Path is:</div>
              <div>
                <strong>
                  {isLoading || isFetching || isNull(count) ? (
                    <LoadingSpinner ariaLoadingLabel="Loading..." isLoading size="$sm" />
                  ) : (
                    <React.Fragment>{count}</React.Fragment>
                  )}
                </strong>
              </div>
            </Layout.Group>

            {count === 0 && (
              <Checkbox
                isChecked={isChecked}
                label="Continue with 0 records"
                onChange={(event) => {
                  return setChecked(event?.target?.checked);
                }}
              />
            )}
          </Modal.Section>
        </Styles>
      </Modal>
    </ErrorHandler>
  );
}
